import React from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm, requiredField } from '../../../utils/validators';
import {
    WeezHeader,
    Button,
    SelectLabel,
    ToastContext,
    SplitContainer,
    Message,
    FrameCard,
    InputLabel,
    Input,
    ToggleLabel,
    ToggleContainer,
    Section,
    CounterLabel,
    SelectHierarchyLabel,
    Form,
    TooltipHelp,
    Card
} from '@weezevent/nacre';
import EventModel from '../../../models/eventModel';
import RatesTreeModel from '../../../models/ratesTreeModel';
import WaitingListModel from '../../../models/waitingListModel';
import IdentityModel from '../../../models/identity';
import Widget from '../../../models/widget';
import MailTemplateModel from '../../../models/mailTemplate';
import Style from './waitingListForm.module.css';
import { stylizeEventOption } from '../../../components/events/select-events/utils';
import RatesPicker from './ratesPicker';
import { Loader } from 'semantic-ui-react';
import { DateTimeInput } from '../../../components/date-time-input/dateTimeInput';
import { convertToSeconds, convertHoursToSeconds, convertToHours, convertToDays } from '../utils';
import SelectEvents from '../../../components/events/select-events/select-events';
import moment from 'moment';

function stylizeIdentityOption(identity, t) {
    return (
        <div className={Style['identity-options']}>
            <div className={Style['identity-from-address']}>
                <div>{identity.from_address.name}</div>
                <div>{`<${identity.from_address.email}>`}</div>
            </div>
            <div className={Style['identity-reply-to']}>
                <div>{t('ticket.waiting_list.create.emails_and_notifications.reply-to')}</div>
                <div>{identity.reply_to.email}</div>
            </div>
        </div>
    );
}

function EventAndRatesPicker({ event, rate, onChangeEvent, onChangeRate, events, rateOptions, formErrors, loadingRates }) {
    const { t } = useTranslation();

    return (
        <Form.Field>
            <div className={Style['selector-container']}>
                <SelectEvents value={event} onChange={onChangeEvent} events={events} />
            </div>
            <Section>
                <SelectHierarchyLabel
                    label={t('ticket.waiting_list.create.purchase_rate.rate_label')}
                    disabled={!event || loadingRates}
                    widthIndent="10px"
                    value={rate}
                    options={rateOptions}
                    placeholder={t('ticket.waiting_list.create.purchase_rate.rate_placeholder')}
                    onChange={onChangeRate}
                />
                <Form.FieldErrors errors={formErrors['target_rate'] ? [t(formErrors['target_rate'])] : null} />
            </Section>
        </Form.Field>
    );
}

function WaitingListGeneralInformation({ form, setForm, waitingList, formErrors }) {
    const { t } = useTranslation();

    return (
        <SplitContainer
            additional={
                <Message mini title={t('ticket.waiting_list.create.general_informations.tips_title')} type="tips">
                    {t('ticket.waiting_list.create.general_informations.tips_subtitle')}
                </Message>
            }
        >
            <FrameCard title={t('ticket.waiting_list.create.general_informations.title')} subtitle={t('ticket.waiting_list.create.general_informations.subtitle')}>
                <div>
                    <Form.Field className={Style['general-information-name']}>
                        <InputLabel label={t('ticket.waiting_list.create.general_informations.input_label')} />
                        <Input
                            value={form.name}
                            placeholder={t('ticket.waiting_list.create.general_informations.input_placeholder')}
                            onChange={({ target: { value } }) => setForm('name', value)}
                        />
                        <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : null} />
                    </Form.Field>
                    {!waitingList && (
                        <div className={Style['toggle-label']}>
                            <ToggleLabel
                                label={t('ticket.waiting_list.create.general_informations.status_label')}
                                checked={form.status === 'active'}
                                onChange={(e, { checked }) => setForm('status', checked ? 'active' : 'inactive')}
                                inlinePosition="left"
                            />
                        </div>
                    )}
                </div>
            </FrameCard>
        </SplitContainer>
    );
}

function WaitingListProcessPurchase({ form, setForm, organization, isProcessPurchase, setIsProcessPurchase, eventTargetId, templatesOptions, widgetOptions, loadingWidgets }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const onToggleProcessing = React.useCallback(
        checked => {
            if (!checked) {
                setForm('next_processing', null);
            }
            setIsProcessPurchase(checked);
        },
        [setForm]
    );

    const _widgetOptions = React.useMemo(() => {
        return widgetOptions.filter(widget => widget.usage === 'resale');
    }, [widgetOptions]);

    return (
        <SplitContainer
            additional={
                <Message mini title={t('ticket.waiting_list.create.process_purchase.tips_title')} type="tips">
                    {t('ticket.waiting_list.create.process_purchase.tips_subtitle')}
                </Message>
            }
        >
            <FrameCard
                className={Style['card-content']}
                title={t('ticket.waiting_list.create.process_purchase.title')}
                subtitle={t('ticket.waiting_list.create.process_purchase.subtitle')}
            >
                <ToggleContainer
                    toggle={isProcessPurchase}
                    onToggle={onToggleProcessing}
                    title={t('ticket.waiting_list.create.process_purchase.mail_template_purchase_link_title')}
                    subtitle={t('ticket.waiting_list.create.process_purchase.mail_template_purchase_link_subtitle')}
                >
                    <Section>
                        <SelectLabel
                            value={form.mail_template_purchase_link}
                            label={t('ticket.waiting_list.create.emails_and_notifications.email_template_label')}
                            placeholder={t('ticket.waiting_list.create.emails_and_notifications.email_template_placeholder')}
                            onChange={(e, { value }) => {
                                setForm('mail_template_purchase_link', value);
                            }}
                            options={templatesOptions}
                        />
                        <DateTimeInput
                            label={
                                <div>
                                    {t('ticket.waiting_list.create.process_purchase.next_processing_date_label')}
                                    <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.process_purchase.next_processing_label_tooltip')} />
                                </div>
                            }
                            onChange={date => {
                                setForm('next_processing', date);
                            }}
                            value={form.next_processing}
                            dateProps={{ minDate: moment() }}
                            locale={locale}
                            timezone={organization.timezone}
                        />
                        <div className={Style['counter']}>
                            <CounterLabel
                                label={
                                    <div>
                                        {t('ticket.waiting_list.create.process_purchase.processing_frequency_title')}
                                        <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.process_purchase.processing_frequency_title_tooltip')} />
                                    </div>
                                }
                                placeholder={0}
                                onChange={(e, value) => setForm('processing_frequency', convertHoursToSeconds(value))}
                                value={convertToHours(form.processing_frequency)}
                                min={0}
                                step={1}
                                hideArrows
                            />
                        </div>
                        <div className={Style['counter']}>
                            <CounterLabel
                                label={
                                    <div>
                                        {t('ticket.waiting_list.create.process_purchase.link_time_validity_title')}
                                        <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.process_purchase.link_time_validity_title_tooltip')} />
                                    </div>
                                }
                                placeholder={0}
                                onChange={(e, value) => setForm('link_time_validity', convertHoursToSeconds(value))}
                                value={convertToHours(form.link_time_validity)}
                                min={1}
                                step={1}
                                hideArrows
                            />
                        </div>
                        <SelectLabel
                            value={form.target_widget}
                            disabled={!eventTargetId || loadingWidgets}
                            label={
                                <div>
                                    {t('ticket.waiting_list.create.process_purchase.target_widget_label')}
                                    <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.process_purchase.target_widget_label_tooltip')} />
                                </div>
                            }
                            placeholder={t('ticket.waiting_list.create.emails_and_notifications.widget_placeholder')}
                            onChange={(e, { value }) => {
                                setForm('target_widget', value);
                            }}
                            options={_widgetOptions}
                        />
                    </Section>
                </ToggleContainer>
            </FrameCard>
        </SplitContainer>
    );
}

function WaitingListSubscriptionManagement({
    form,
    setForm,
    setFormObject,
    organization,
    isProcessNotification,
    setIsProcessNotification,
    eventTargetId,
    templatesOptions,
    widgetOptions,
    loadingWidgets,
    identitiesOptions
}) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const onToggleNotification = React.useCallback(
        checked => {
            if (!checked) {
                setForm('next_notification', null);
            }
            setIsProcessNotification(checked);
        },
        [setForm]
    );

    const _widgetOptions = React.useMemo(() => {
        return widgetOptions.filter(widget => widget.usage === 'personal_space');
    }, [widgetOptions]);

    return (
        <SplitContainer
            additional={
                <Message mini title={t('ticket.waiting_list.create.emails_and_notifications.tips_title')} type="tips">
                    {t('ticket.waiting_list.create.emails_and_notifications.tips_subtitle')}
                </Message>
            }
        >
            <FrameCard title={t('ticket.waiting_list.create.emails_and_notifications.title')} subtitle={t('ticket.waiting_list.create.emails_and_notifications.subtitle')}>
                <div>
                    <SelectLabel
                        value={form.ordering_mode}
                        label={t('ticket.waiting_list.create.general_informations.select_label')}
                        onChange={(e, { value }) => {
                            setForm('ordering_mode', value);
                        }}
                        options={[
                            { text: t('ticket.waiting_list.create.general_informations.sorted'), value: 'sorted' },
                            { text: t('ticket.waiting_list.create.general_informations.random'), value: 'random' }
                        ]}
                    />
                    <SelectLabel
                        value={form.identity_id || identitiesOptions[0]?.value} // default option is selected if not set
                        label={
                            <div>
                                {t('ticket.waiting_list.create.emails_and_notifications.sender_label')}
                                <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.emails_and_notifications.sender_label_tooltip')} />
                            </div>
                        }
                        placeholder={t('ticket.waiting_list.create.emails_and_notifications.sender_placeholder')}
                        onChange={(e, { value }) => {
                            setForm('identity_id', value);
                        }}
                        options={identitiesOptions}
                    />
                    <SelectLabel
                        useNew
                        value={form.personal_space_widget}
                        disabled={!eventTargetId || loadingWidgets}
                        label={
                            <div>
                                {t('ticket.waiting_list.create.emails_and_notifications.widget_label')}
                                <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.emails_and_notifications.widget_label_tooltip')} />
                            </div>
                        }
                        placeholder={t('ticket.waiting_list.create.emails_and_notifications.widget_placeholder')}
                        onChange={(e, { value }) => {
                            setForm('personal_space_widget', value);
                        }}
                        options={_widgetOptions}
                        onClear={() =>
                            setFormObject(
                                {
                                    personal_space_widget: null,
                                    optin_subscription_required: false,
                                    optin_notification_required: false
                                },
                                true
                            )
                        }
                    />
                    <Card className={Style['card-content']}>
                        <Card.Content>
                            <ToggleContainer
                                toggle={form.send_mail_subscription}
                                onToggle={checked => setForm('send_mail_subscription', checked)}
                                title={t('ticket.waiting_list.create.emails_and_notifications.send_mail_subscription_title')}
                                subtitle={t('ticket.waiting_list.create.emails_and_notifications.send_mail_subscription_subtitle')}
                            >
                                <Section>
                                    <SelectLabel
                                        value={form.mail_template_subscription}
                                        label={t('ticket.waiting_list.create.emails_and_notifications.email_template_label')}
                                        placeholder={t('ticket.waiting_list.create.emails_and_notifications.email_template_placeholder')}
                                        onChange={(e, { value }) => {
                                            setForm('mail_template_subscription', value);
                                        }}
                                        options={templatesOptions}
                                    />
                                    <ToggleLabel
                                        label={t('ticket.waiting_list.create.emails_and_notifications.optin_subscription_required_title')}
                                        subtitle={t('ticket.waiting_list.create.emails_and_notifications.optin_subscription_required_subtitle')}
                                        checked={form.optin_subscription_required}
                                        disabled={!form.personal_space_widget}
                                        onChange={(e, { checked }) => setForm('optin_subscription_required', checked)}
                                        inlinePosition="left"
                                    />
                                </Section>
                            </ToggleContainer>
                        </Card.Content>
                    </Card>
                    <Card className={Style['card-content']}>
                        <Card.Content>
                            <ToggleContainer
                                toggle={isProcessNotification}
                                onToggle={onToggleNotification}
                                title={t('ticket.waiting_list.create.emails_and_notifications.next_notification_title')}
                                subtitle={t('ticket.waiting_list.create.emails_and_notifications.next_notification_subtitle')}
                            >
                                <Section>
                                    <SelectLabel
                                        value={form.mail_template_notification}
                                        label={t('ticket.waiting_list.create.emails_and_notifications.email_template_label')}
                                        placeholder={t('ticket.waiting_list.create.emails_and_notifications.email_template_placeholder')}
                                        onChange={(e, { value }) => {
                                            setForm('mail_template_notification', value);
                                        }}
                                        options={templatesOptions}
                                    />
                                    <DateTimeInput
                                        label={
                                            <div>
                                                {t('ticket.waiting_list.create.emails_and_notifications.next_notification_date_label')}
                                                <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.emails_and_notifications.next_notification_label_tooltip')} />
                                            </div>
                                        }
                                        onChange={date => {
                                            setForm('next_notification', date);
                                        }}
                                        value={form.next_notification}
                                        dateProps={{ minDate: moment() }}
                                        locale={locale}
                                        timezone={organization.timezone}
                                    />
                                    <div className={Style['counter']}>
                                        <CounterLabel
                                            label={
                                                <div>
                                                    {t('ticket.waiting_list.create.emails_and_notifications.notification_frequency_title')}
                                                    <TooltipHelp
                                                        triggerOnHover
                                                        title={t('ticket.waiting_list.create.emails_and_notifications.notification_frequency_title_tooltip')}
                                                    />
                                                </div>
                                            }
                                            placeholder={0}
                                            onChange={(e, value) => setForm('notification_frequency', convertToSeconds(value))}
                                            value={convertToDays(form.notification_frequency)}
                                            min={0}
                                            step={1}
                                            hideArrows
                                        />
                                    </div>
                                    <ToggleContainer
                                        toggle={form.optin_notification_required}
                                        onToggle={checked => setForm('optin_notification_required', checked)}
                                        disabled={!form.personal_space_widget}
                                        title={t('ticket.waiting_list.create.emails_and_notifications.optin_subscription_required_title')}
                                        subtitle={t('ticket.waiting_list.create.emails_and_notifications.optin_subscription_required_subtitle')}
                                    >
                                        <div className={Style['counter']}>
                                            <CounterLabel
                                                label={
                                                    <div>
                                                        {t('ticket.waiting_list.create.emails_and_notifications.optin_delay_title')}
                                                        <TooltipHelp triggerOnHover title={t('ticket.waiting_list.create.emails_and_notifications.optin_delay_title_tooltip')} />
                                                    </div>
                                                }
                                                placeholder={0}
                                                onChange={(e, value) => setForm('optin_delay', value * 24 * 60 * 60)}
                                                value={convertToDays(form.optin_delay)}
                                                min={0}
                                                step={1}
                                                hideArrows
                                            />
                                        </div>
                                    </ToggleContainer>
                                </Section>
                            </ToggleContainer>
                        </Card.Content>
                    </Card>
                </div>
            </FrameCard>
        </SplitContainer>
    );
}

function WaitingListPurchaseRate({ form, setForm, setFormObject, eventTargetId, setEventTargetId, ratesTree, events, formErrors, loadingRates }) {
    const { t } = useTranslation();

    const onChangeEvent = React.useCallback(
        (e, event) => {
            setEventTargetId(event.value);
            setFormObject({ target_rate: null, personal_space_widget: null, target_widget: null, rates: [] }, true);
        },
        [setFormObject]
    );

    const ratesOptionsFiltered = React.useMemo(() => {
        // // Rate that are already selected as target rate for waiting list cannot be selected
        if (!ratesTree) return;

        const ratesOptions = ratesTree.toOptions({
            filter: rate => !rate.is_waiting_list_target || rate.pk === form.target_rate,
            extraRate: rate => ({
                is_waiting_list_target: rate.is_waiting_list_target
            })
        });
        return ratesOptions;
    }, [form, ratesTree]);

    return (
        <SplitContainer
            additional={
                <Message mini title={t('ticket.waiting_list.create.purchase_rate.tips_title')} type="tips">
                    {t('ticket.waiting_list.create.purchase_rate.tips_subtitle')}
                </Message>
            }
        >
            <FrameCard title={t('ticket.waiting_list.create.purchase_rate.title')} subtitle={t('ticket.waiting_list.create.purchase_rate.subtitle')}>
                <div className={Style['purchase-rate-container']}>
                    <EventAndRatesPicker
                        event={eventTargetId}
                        rate={form.target_rate}
                        onChangeEvent={onChangeEvent}
                        onChangeRate={(e, { value }) => {
                            setForm('target_rate', value);
                        }}
                        events={events}
                        rateOptions={ratesOptionsFiltered}
                        formErrors={formErrors}
                        loadingRates={loadingRates}
                    />
                    <div className={Style['counter']}>
                        <CounterLabel
                            label={t('ticket.waiting_list.create.purchase_rate.quantity_label')}
                            numberDecimals={0}
                            min={1}
                            max={100}
                            step={1}
                            onChange={(e, value) => setForm('max_quantity_requested', value)}
                            value={form.max_quantity_requested}
                            hideArrows
                        />
                    </div>
                </div>
            </FrameCard>
        </SplitContainer>
    );
}

function WaitingListSubscriptionsRates({ form, setForm, event, organization, waitingListId }) {
    const { t } = useTranslation();

    const linkedRates = React.useMemo(() => {
        if (!form.rates) {
            return [];
        }
        return form.rates.map(rate => rate.id || rate);
    }, [form]);

    return (
        <SplitContainer
            additional={
                <Message mini title={t('ticket.waiting_list.create.subscription_rate.tips_title')} type="tips">
                    {t('ticket.waiting_list.create.subscription_rate.tips_subtitle')}
                </Message>
            }
        >
            <FrameCard title={t('ticket.waiting_list.create.subscription_rate.title')} subtitle={t('ticket.waiting_list.create.subscription_rate.subtitle')}>
                <div className={Style['purchase-rate-container']}>
                    {event && <RatesPicker organization={organization} event={event} linkedRates={linkedRates} setForm={setForm} waitingListId={waitingListId} />}
                </div>
            </FrameCard>
        </SplitContainer>
    );
}

export default function WaitingListForm({ organization, waitingListId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [eventTargetId, setEventTargetId] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [form, setForm, setFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            ordering_mode: 'sorted',
            status: 'active',
            target_rate: null,
            max_quantity_requested: 1,
            email_container: true,
            identity_id: null,
            mail_template_subscription: null,
            rates: [],
            send_mail_subscription: true,
            optin_subscription_required: false,
            personal_space_widget: null,
            next_notification: null,
            mail_template_notification: null,
            optin_notification_required: false,
            optin_delay: 604800,
            notification_frequency: 604800,
            next_processing: null,
            mail_template_purchase_link: null,
            processing_frequency: 86400,
            link_time_validity: 86400,
            target_widget: null
        },
        {
            name: requiredField,
            target_rate: requiredField
        }
    );

    const [isProcessNotification, setIsProcessNotification] = React.useState(form.next_notification);
    const [isProcessPurchase, setIsProcessPurchase] = React.useState(form.next_processing);

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            query: {
                time_status: 'ongoing, upcoming'
            }
        },
        [organization]
    );

    const [identities, loadingIdentities] = IdentityModel.useApiModel(
        {
            organizationId: organization.id
        },
        [organization]
    );

    const [emailTemplates, loadingEmailTemplates] = MailTemplateModel.useApiModel(
        {
            organizationId: organization.id,
            key: 'crm-mail'
        },
        [organization]
    );

    const [ratesTree, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: eventTargetId,
            launch: Boolean(organization.id && eventTargetId)
        },
        [organization, eventTargetId]
    );

    const [waitingList, loadingWaitingList] = WaitingListModel.useApiModel(
        {
            organizationId: organization.id,
            id: waitingListId,
            launch: Boolean(waitingListId),
            cache: false,
            allow_cache: false,
            query: {
                expand: 'target_rate,rates'
            },
            onSuccess: waitingList => {
                setFormObject({
                    name: waitingList.name,
                    ordering_mode: waitingList.ordering_mode,
                    target_rate: waitingList.target_rate.id,
                    max_quantity_requested: waitingList.max_quantity_requested,
                    email_container: waitingList.identity_id || waitingList.mail_template_subscription,
                    identity_id: waitingList.identity_id,
                    mail_template_subscription: waitingList.mail_template_subscription,
                    rates: waitingList.rates.map(rate => rate.id),
                    send_mail_subscription: waitingList.send_mail_subscription,
                    optin_subscription_required: waitingList.optin_subscription_required,
                    personal_space_widget: waitingList.personal_space_widget,
                    next_notification: waitingList.next_notification,
                    mail_template_notification: waitingList.mail_template_notification,
                    optin_notification_required: waitingList.optin_notification_required,
                    optin_delay: waitingList.optin_delay === null ? 0 : waitingList.optin_delay,
                    notification_frequency: waitingList.notification_frequency === null ? 0 : waitingList.notification_frequency,
                    next_processing: waitingList.next_processing,
                    mail_template_purchase_link: waitingList.mail_template_purchase_link,
                    processing_frequency: waitingList.processing_frequency === null ? 0 : waitingList.processing_frequency,
                    link_time_validity: waitingList.link_time_validity,
                    target_widget: waitingList.target_widget
                });
                setEventTargetId(waitingList.target_rate.event.id);
                setIsProcessNotification(waitingList.next_notification);
                setIsProcessPurchase(waitingList.next_processing);
            }
        },
        [organization, waitingListId]
    );

    const [widgets, loadingWidgets] = Widget.useApiModel(
        {
            organizationId: organization.pk,
            eventId: eventTargetId,
            launch: Boolean(eventTargetId),
            cache: false,
            allow_cache: false,
            query: {
                usage__in: ['personal_space', 'resale']
            }
        },
        [eventTargetId]
    );

    const eventOptions = React.useMemo(() => {
        if (!events) return [];
        return (
            events &&
            events.map(e => {
                return {
                    key: e.id,
                    text: stylizeEventOption(e),
                    name: e.name,
                    value: e.id
                };
            })
        );
    }, [events]);

    const widgetOptions = React.useMemo(() => {
        if (!widgets) return [];
        return (
            widgets &&
            widgets.map(w => {
                return {
                    key: w.id,
                    text: w.name,
                    value: w.id,
                    usage: w.usage
                };
            })
        );
    }, [widgets]);

    const identitiesOptions = React.useMemo(() => {
        let sortedIdentities = identities?.sort((a, b) => a.from_address.name.toLowerCase().localeCompare(b.from_address.name.toLowerCase())) || [];
        sortedIdentities = sortedIdentities.map(e => {
            return {
                key: e.id,
                text: stylizeIdentityOption(e, t),
                value: e.id
            };
        });
        // identity by default option --> Weezevent
        const defaultValue = {
            key: 0,
            text: t('ticket.waiting_list.create.emails_and_notifications.default_identity'),
            value: null
        };
        // default option is placed as first option
        sortedIdentities.unshift(defaultValue);
        return sortedIdentities;
    }, [identities]);

    const templatesOptions = React.useMemo(() => {
        if (!emailTemplates) return [];
        let _templatesOptions = [
            {
                key: 0,
                value: null,
                text: t('ticket.waiting_list.create.emails_and_notifications.default_email')
            }
        ];
        emailTemplates &&
            emailTemplates.forEach(e => {
                _templatesOptions.push({
                    key: e.id,
                    text: e.name,
                    value: e.id
                });
            });
        return _templatesOptions;
    }, [emailTemplates]);

    const onSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        if (!formValidate()) {
            return;
        }
        const _rates = form.rates.map(rate => rate.id || rate);
        const _form = {
            ...form,
            rates: _rates,
            optin_delay: form.optin_delay === 0 ? null : form.optin_delay,
            notification_frequency: form.notification_frequency === 0 ? null : form.notification_frequency,
            processing_frequency: form.processing_frequency === 0 ? null : form.processing_frequency
        };

        let _waitingList = new WaitingListModel({ ..._form, id: waitingListId });
        _waitingList
            .save({ organizationId: organization.pk })
            .then(() => {
                toast.success(t(`ticket.waiting_list.create.toast-success${waitingListId ? '-edit' : ''}`));
                history.push(`/ticket/O${organization.pk}/waiting_lists`);
            })
            .catch(() => {
                toast.error(t(`ticket.waiting_list.create.toast-error${waitingListId ? '-edit' : ''}`));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [formValidate, form, organization, waitingListId, toast, t]);

    const disabledButton = React.useMemo(() => {
        return (
            !form.name ||
            !form.target_rate ||
            ((form.send_mail_subscription || isProcessNotification) && !form.personal_space_widget) ||
            (isProcessNotification && !form.next_notification) ||
            (isProcessPurchase && (!form.next_processing || !form.target_widget))
        );
    }, [form, isProcessNotification, isProcessPurchase]);

    if (loadingEvents || loadingEmailTemplates || loadingIdentities || (waitingListId && loadingWaitingList)) {
        return (
            <div style={{ height: '50vh' }}>
                <Loader active />
            </div>
        );
    }

    return (
        <>
            <WeezHeader
                title={waitingList ? waitingList.name : t('ticket.waiting_list.create.title')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.pk}/waiting_lists`) }}
                rightComponent={[
                    <Button key={0} onClick={onSubmit} primary disabled={disabledButton} isLoading={isSubmitting}>
                        {t('ticket.common.action.save')}
                    </Button>
                ]}
            />
            <WaitingListGeneralInformation form={form} setForm={setForm} waitingList={waitingList} formErrors={formErrors} />
            <WaitingListPurchaseRate
                form={form}
                setForm={setForm}
                setFormObject={setFormObject}
                eventTargetId={eventTargetId}
                setEventTargetId={setEventTargetId}
                events={events}
                ratesTree={ratesTree}
                formErrors={formErrors}
                loadingRates={loadingRates}
            />
            {eventTargetId && (
                <WaitingListSubscriptionsRates
                    form={form}
                    setForm={setForm}
                    event={events.find(evt => evt.id === eventTargetId)}
                    organization={organization}
                    eventOptions={eventOptions}
                    waitingListId={waitingListId}
                />
            )}
            <WaitingListSubscriptionManagement
                form={form}
                setForm={setForm}
                setFormObject={setFormObject}
                eventTargetId={eventTargetId}
                organization={organization}
                templatesOptions={templatesOptions}
                widgetOptions={widgetOptions}
                loadingWidgets={loadingWidgets}
                isProcessNotification={isProcessNotification}
                setIsProcessNotification={setIsProcessNotification}
                identitiesOptions={identitiesOptions}
            />
            <WaitingListProcessPurchase
                form={form}
                setForm={setForm}
                eventTargetId={eventTargetId}
                organization={organization}
                templatesOptions={templatesOptions}
                widgetOptions={widgetOptions}
                loadingWidgets={loadingWidgets}
                isProcessPurchase={isProcessPurchase}
                setIsProcessPurchase={setIsProcessPurchase}
            />
        </>
    );
}
